import React from 'react'
import {
	Button,
	Flex,
	Heading,
	IconButton,
	Modal,
	ModalCloseButton,
	ModalContent,
	ModalOverlay,
	Radio,
	RadioGroup,
	Stack,
	Text,
	Tooltip,
} from '@chakra-ui/react'
import { EyeIcon } from 'app/icons/EyeIcon'
import { FileIcon } from 'app/icons/FileIcon'
import { Payment } from 'data/Payment'
import api from 'services/api'
import {
	BoletoBillTo,
	useGenerateAsaasBoletoMutation,
} from '../queries/useGenerateAsaasBoletoMutation'

interface Props {
	payment: Payment
	compact?: boolean
}

interface LegalRepresentative {
	name?: string
	cpf?: string
	cnpj?: string
}

export function GenerateAsaasBoletoButton({ payment, compact }: Props) {
	const mutation = useGenerateAsaasBoletoMutation()
	const [loadingCustomer, setLoadingCustomer] = React.useState(false)
	const [representative, setRepresentative] =
		React.useState<LegalRepresentative | null>(null)
	const [billTo, setBillTo] = React.useState<BoletoBillTo>('responsible')

	// When the patient has a legal representative, ask who the boleto is issued to
	// (defaults to the representative). Otherwise generate straight away.
	async function handleGenerate() {
		const customerId = payment.customer?.id
		if (customerId) {
			setLoadingCustomer(true)
			try {
				const response = await api.get(`/customers/${customerId}`)
				const rep = response.data?.legalRepresentatives?.[0]
				if (rep?.name || rep?.cpf || rep?.cnpj) {
					setBillTo('responsible')
					setRepresentative(rep)
					return
				}
			} catch {
				// Could not inspect the customer: let the API pick the default.
			} finally {
				setLoadingCustomer(false)
			}
		}
		mutation.mutate({ paymentId: payment.id })
	}

	function confirmGenerate() {
		mutation.mutate({ paymentId: payment.id, billTo })
		setRepresentative(null)
	}

	if (payment.asaas?.bankSlipUrl || payment.asaas?.invoiceUrl) {
		return (
			<Tooltip label="Visualizar boleto">
				<IconButton
					as="a"
					href={payment.asaas.bankSlipUrl ?? payment.asaas.invoiceUrl}
					target="_blank"
					display="flex"
					variant="unstyled"
					color="#0C6"
					bg="#f9f9f9"
					_dark={{ bg: '#223344' }}
					aria-label="Visualizar boleto"
					icon={<EyeIcon size={20} />}
				/>
			</Tooltip>
		)
	}

	const isLoading = loadingCustomer || mutation.isPending

	const billToDialog = (
		<Modal
			isOpen={!!representative}
			onClose={() => setRepresentative(null)}
			isCentered
		>
			<ModalOverlay />
			<ModalContent px="6" py="6">
				<Flex alignItems="center" justify="space-between" mb="4">
					<Heading fontSize="lg">Emitir boleto para</Heading>
					<ModalCloseButton size="md" top="5" />
				</Flex>
				<Text fontSize="sm" mb="4">
					Este paciente possui responsável cadastrado. Em nome de quem
					o boleto deve sair?
				</Text>
				<RadioGroup
					value={billTo}
					onChange={(value) => setBillTo(value as BoletoBillTo)}
					mb="6"
				>
					<Stack spacing="3">
						<Radio value="responsible" colorScheme="primary">
							Responsável
							{representative?.name
								? ` — ${representative.name}`
								: ''}
						</Radio>
						<Radio value="patient" colorScheme="primary">
							Paciente
							{payment.customer?.name
								? ` — ${payment.customer.name}`
								: ''}
						</Radio>
					</Stack>
				</RadioGroup>
				<Flex justify="space-evenly" flexDir="row-reverse">
					<Button
						size="sm"
						w="130px"
						h="32px"
						colorScheme="primary"
						textTransform="uppercase"
						isLoading={mutation.isPending}
						onClick={confirmGenerate}
					>
						Gerar
					</Button>
					<Button
						size="sm"
						w="130px"
						h="32px"
						colorScheme="primary"
						variant="outline"
						textTransform="uppercase"
						onClick={() => setRepresentative(null)}
					>
						Cancelar
					</Button>
				</Flex>
			</ModalContent>
		</Modal>
	)

	if (compact) {
		return (
			<>
				<Tooltip label="Gerar boleto Asaas">
					<IconButton
						colorScheme="primary"
						variant="outline"
						aria-label="Gerar boleto Asaas"
						icon={<FileIcon size={18} />}
						isLoading={isLoading}
						onClick={handleGenerate}
					/>
				</Tooltip>
				{billToDialog}
			</>
		)
	}

	return (
		<>
			<Button
				size="xs"
				colorScheme="primary"
				variant="outline"
				textTransform="uppercase"
				isLoading={isLoading}
				onClick={handleGenerate}
			>
				Gerar boleto
			</Button>
			{billToDialog}
		</>
	)
}
